import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { transactionsAPI, categoriesAPI, accountsAPI } from '../lib/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Plus, Search, Filter, ArrowUpRight, ArrowDownRight,
  CalendarIcon, Trash2, Edit2, X, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const Transactions = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);

  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);

  const [filters, setFilters] = useState({
    type: 'all',
    category_id: 'all',
    account_id: 'all',
    search: ''
  });

  const [form, setForm] = useState({
    type: 'expense',
    amount: '',
    category_id: '',
    account_id: '',
    description: '',
    date: new Date(),
    notes: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [txRes, catRes, accRes] = await Promise.all([
        transactionsAPI.getAll({ limit: 100 }),
        categoriesAPI.getAll(),
        accountsAPI.getAll()
      ]);
      setTransactions(txRes.data);
      setCategories(catRes.data);
      setAccounts(accRes.data);
    } catch (error) {
      toast.error('Error al cargar transacciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Check if we should open new transaction dialog
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === 'true') {
      setDialogOpen(true);
      window.history.replaceState({}, '', '/transactions');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.amount || !form.category_id || !form.account_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        date: form.date.toISOString()
      };

      if (editingTx) {
        await transactionsAPI.update(editingTx.id, payload);
        toast.success(t('transactionUpdated'));
      } else {
        await transactionsAPI.create(payload);
        toast.success(t('transactionCreated'));
      }

      setDialogOpen(false);
      setEditingTx(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta transacción?')) return;

    try {
      await transactionsAPI.delete(id);
      toast.success(t('transactionDeleted'));
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (tx) => {
    setEditingTx(tx);
    setForm({
      type: tx.type,
      amount: tx.amount.toString(),
      category_id: tx.category_id,
      account_id: tx.account_id,
      description: tx.description,
      date: new Date(tx.date),
      notes: tx.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      type: 'expense',
      amount: '',
      category_id: accounts[0]?.id || '',
      account_id: categories[0]?.id || '',
      description: '',
      date: new Date(),
      notes: ''
    });
  };

  const openNewDialog = () => {
    setEditingTx(null);
    resetForm();
    setDialogOpen(true);
  };

  const filteredTransactions = transactions.filter(tx => {
    if (filters.type !== 'all' && tx.type !== filters.type) return false;
    if (filters.category_id !== 'all' && tx.category_id !== filters.category_id) return false;
    if (filters.account_id !== 'all' && tx.account_id !== filters.account_id) return false;
    if (filters.search && !tx.description.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';
  const getAccountName = (id) => accounts.find(a => a.id === id)?.name || '-';
  const getCategoryColor = (id) => categories.find(c => c.id === id)?.color || '#64748B';

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            {t('transactions')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {filteredTransactions.length} transacciones
          </p>
        </div>
        <Button
          onClick={openNewDialog}
          className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
          data-testid="new-transaction-btn"
        >
          <Plus size={16} className="mr-2" />
          {t('newTransaction')}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 rounded-sm shadow-none" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={t('search')}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>

            <Select value={filters.type} onValueChange={(v) => setFilters(prev => ({ ...prev, type: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="type-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="income">{t('income')}</SelectItem>
                <SelectItem value="expense">{t('expense')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.category_id} onValueChange={(v) => setFilters(prev => ({ ...prev, category_id: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="category-filter">
                <SelectValue placeholder={t('category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.account_id} onValueChange={(v) => setFilters(prev => ({ ...prev, account_id: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="account-filter">
                <SelectValue placeholder={t('account')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {accounts.map(acc => (
                  <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card className="border-slate-200 rounded-sm shadow-none" data-testid="transactions-list">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <RefreshCw className="w-6 h-6 animate-spin text-emerald-900" />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <p>No hay transacciones</p>
              <Button variant="link" onClick={openNewDialog} className="text-emerald-900">
                Crear primera transacción
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredTransactions.map(tx => (
                <div
                  key={tx.id}
                  className="flex items-start gap-3 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5",
                    tx.type === 'income' ? 'bg-emerald-50' : 'bg-red-50'
                  )}>
                    {tx.type === 'income'
                      ? <ArrowUpRight className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                      : <ArrowDownRight className="w-4 h-4 text-red-500" strokeWidth={1.5} />
                    }
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                      <p className="font-medium text-slate-900 text-sm truncate max-w-[140px] sm:max-w-none">
                        {tx.description || getCategoryName(tx.category_id)}
                      </p>
                      <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: `${getCategoryColor(tx.category_id)}15`, color: getCategoryColor(tx.category_id) }}
                      >
                        {getCategoryName(tx.category_id)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      {getAccountName(tx.account_id)} · {formatDate(tx.date)}
                    </p>
                    <p className={cn("font-mono font-semibold text-sm mt-1",
                      tx.type === 'income' ? 'text-emerald-600' : 'text-red-500'
                    )}>
                      {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount, user?.currency)}
                    </p>
                  </div>

                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(tx)} className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-900" data-testid={`edit-tx-${tx.id}`}>
                      <Edit2 size={15} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(tx.id)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" data-testid={`delete-tx-${tx.id}`}>
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingTx ? t('editTransaction') : t('newTransaction')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type selector */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={form.type === 'expense' ? 'default' : 'outline'}
                className={cn(
                  "rounded-sm",
                  form.type === 'expense' && 'bg-red-500 hover:bg-red-600'
                )}
                onClick={() => setForm(prev => ({ ...prev, type: 'expense' }))}
                data-testid="type-expense-btn"
              >
                <ArrowDownRight size={16} className="mr-2" />
                {t('expense')}
              </Button>
              <Button
                type="button"
                variant={form.type === 'income' ? 'default' : 'outline'}
                className={cn(
                  "rounded-sm",
                  form.type === 'income' && 'bg-emerald-600 hover:bg-emerald-700'
                )}
                onClick={() => setForm(prev => ({ ...prev, type: 'income' }))}
                data-testid="type-income-btn"
              >
                <ArrowUpRight size={16} className="mr-2" />
                {t('income')}
              </Button>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('amount')} *
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {user?.currency === 'EUR' ? '€' : '$'}
                </span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-8 font-mono rounded-sm"
                  placeholder="0.00"
                  required
                  data-testid="amount-input"
                />
              </div>
            </div>

            {/* Category and Account */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  {t('category')} *
                </Label>
                <Select value={form.category_id} onValueChange={(v) => setForm(prev => ({ ...prev, category_id: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="category-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  {t('account')} *
                </Label>
                <Select value={form.account_id} onValueChange={(v) => setForm(prev => ({ ...prev, account_id: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="account-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('description')}
              </Label>
              <Input
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-sm"
                placeholder="Descripción breve"
                data-testid="description-input"
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('date')}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left rounded-sm" data-testid="date-picker">
                    <CalendarIcon size={16} className="mr-2 text-slate-400" />
                    {format(form.date, 'PPP', { locale: es })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={form.date}
                    onSelect={(date) => date && setForm(prev => ({ ...prev, date }))}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
                data-testid="save-transaction-btn"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transactions;
